/**
 * Generic component to display a infinitely scrollable container of data.
 */
import { DocumentNode, ErrorLike, ErrorPolicy, NetworkStatus, WatchQueryFetchPolicy } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { parseRows } from "../../graphql/util";
import { handleInfiniteScroll } from "../../lib/Util";
import { ScrollToTop } from "../ui/buttons";
import { ScrollablePageContainer } from "../ui/common";

type ChildrenOptions<T> = {
  loading: boolean;
  error?: ErrorLike;
  networkStatus: NetworkStatus;
  results: T[] | null;
  hasResults: boolean;
  isLoadingMore: boolean;
};

type Variables = {
  [key: string]: string | boolean | number | null | undefined | string | number | boolean | string[];
};

interface Props<T> {
  first: number;
  className: string;
  query: DocumentNode;
  variables: Variables;
  errorPolicy?: ErrorPolicy;
  fetchPolicy?: WatchQueryFetchPolicy;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  nodeName: string;
  children: (options: ChildrenOptions<T>) => ReactNode;
  queryOptions?: useQuery.Options;
  setApplyTransition?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const InfiniteScrollContainer = <T,>({
  first,
  query,
  className,
  variables,
  queryOptions,
  errorPolicy = "all",
  fetchPolicy = "cache-and-network",
  nextFetchPolicy = "cache-first",
  nodeName,
  children,
  setApplyTransition,
}: Props<T>) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showScrollBackButton, setShowScrollBackButton] = useState(false);

  const containerRef = useRef<null | HTMLDivElement>(null);

  const queryResults = useQuery(query, {
    variables,
    errorPolicy,
    fetchPolicy,
    nextFetchPolicy,
    ...queryOptions,
  });

  const { results, hasResults, isPageable } = parseRows<T>(queryResults.data, nodeName, null);

  const onLoadMore = () => {
    setIsLoadingMore(true);

    queryResults
      .fetchMore({
        variables: {
          first,
          after: (queryResults.data as Record<string, { pageInfo: { endCursor: string } }>)[nodeName].pageInfo
            .endCursor,
        },
      })
      .then(() => setIsLoadingMore(false));
  };

  const scrollToTop = () => {
    containerRef?.current?.scrollTo({ top: 0, behavior: "smooth" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const toggleScrollBackButtonVisibility = () => {
      setShowScrollBackButton(containerRef?.current?.scrollTop !== undefined && containerRef.current.scrollTop > 0);
      if (setApplyTransition) {
        setApplyTransition(containerRef?.current?.scrollTop !== undefined && containerRef.current.scrollTop > 0);
      }
    };

    // the container ref's current may change during this useEffect. We copy it into a const to prevent it changing on us.
    const currentContainer = containerRef.current;
    currentContainer?.addEventListener("scroll", toggleScrollBackButtonVisibility);

    return () => {
      currentContainer?.removeEventListener("scroll", toggleScrollBackButtonVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ScrollablePageContainer
        className={className}
        onScroll={(e) => handleInfiniteScroll(e, onLoadMore, isPageable)}
        ref={containerRef}
        data-cy={className}
      >
        {children({
          results,
          hasResults,
          isLoadingMore,
          ...queryResults,
        })}
      </ScrollablePageContainer>
      {showScrollBackButton && <ScrollToTop onClick={scrollToTop} />}
    </>
  );
};
